import React, { useMemo } from 'react';
import {
    BottomNavigation,
    BottomNavigationAction,
    Paper,
    Box,
    useTheme,
    useMediaQuery,
    Badge,
    Fab,
    Zoom,
    Tooltip,
} from '@mui/material';
import {
    Add as AddIcon,
    Menu as MenuIcon,
    AttachMoney,
    Assignment,
    Schedule,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getNavigationItems } from '../navigation/EnhancedNavigation';
import { filterDeliveryCockpitNavItems } from '../../constants/deliveryCockpitNav';

/** Max primary tabs before the overflow Menu action (delivery-cockpit wedge). */
export const MOBILE_BOTTOM_NAV_MAX_ITEMS = 4;

const MENU_ACTION_VALUE = '__menu__';

const shortNavLabel = (label) => {
    if (label === 'Expense Management') return 'Expenses';
    if (label === 'Billing & Invoices') return 'Invoices';
    if (label === 'My Workspace') return 'Workspace';
    return label;
};

/**
 * Resolves bottom-nav tabs from the same wedge IA as the sidebar drawer.
 *
 * @param {string|null|undefined} userRole - Authenticated user role.
 * @returns {Array<{ id: string, label: string, icon: React.ComponentType, path: string }>}
 */
export const getMobileBottomNavItems = (userRole) => (
    filterDeliveryCockpitNavItems(getNavigationItems(userRole)).slice(0, MOBILE_BOTTOM_NAV_MAX_ITEMS)
);

const MobileBottomNavigation = ({ onMenuToggle, notificationCount = 0 }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();

    const navItems = useMemo(
        () => getMobileBottomNavItems(user?.role),
        [user?.role],
    );

    if (!isMobile || navItems.length === 0) {
        return null;
    }

    const getCurrentValue = () => {
        const path = location.pathname;
        const activeItem = navItems.find((item) => (
            path === item.path
            || (item.path !== '/dashboard' && path.startsWith(item.path))
        ));
        return activeItem?.id ?? false;
    };

    const handleNavigation = (value) => {
        if (value === MENU_ACTION_VALUE) {
            onMenuToggle?.();
            return;
        }
        const item = navItems.find((navItem) => navItem.id === value);
        if (item) {
            navigate(item.path);
        }
    };

    const handleQuickAction = () => {
        const currentPath = location.pathname;
        if (currentPath.includes('/timesheets')) {
            navigate('/timesheets/new');
        } else if (currentPath.includes('/expenses')) {
            navigate('/expenses/new');
        } else if (currentPath.includes('/project-operations') || currentPath.includes('/projects')) {
            navigate('/project-management?action=create');
        } else if (currentPath.includes('/invoice-management')) {
            navigate('/invoice-management');
        } else if (currentPath.includes('/my-workspace')) {
            navigate('/timesheets');
        } else {
            navigate('/timesheets');
        }
    };

    const getQuickActionIcon = () => {
        const currentPath = location.pathname;
        if (currentPath.includes('/timesheets')) return <AddIcon />;
        if (currentPath.includes('/expenses')) return <AttachMoney />;
        if (currentPath.includes('/project-operations') || currentPath.includes('/projects')) return <Assignment />;
        if (currentPath.includes('/my-workspace')) return <Schedule />;
        return <Schedule />;
    };

    return (
        <Box sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: theme.zIndex.appBar }}>
            <Zoom in>
                <Tooltip title="Quick Action" placement="top">
                    <Fab
                        color="primary"
                        size="large"
                        onClick={handleQuickAction}
                        sx={{
                            position: 'absolute',
                            bottom: 80,
                            right: 16,
                            zIndex: theme.zIndex.fab,
                        }}
                    >
                        {getQuickActionIcon()}
                    </Fab>
                </Tooltip>
            </Zoom>

            <Paper
                elevation={3}
                sx={{
                    borderRadius: 0,
                    pb: 'env(safe-area-inset-bottom)',
                }}
            >
                <BottomNavigation
                    value={getCurrentValue()}
                    onChange={(event, newValue) => handleNavigation(newValue)}
                    showLabels
                    sx={{
                        height: 64,
                        '& .MuiBottomNavigationAction-root': {
                            minWidth: 0,
                            maxWidth: 'none',
                            flex: 1,
                        },
                        '& .MuiBottomNavigationAction-label': {
                            fontSize: '0.75rem',
                            mt: 0.5,
                        },
                    }}
                >
                    {navItems.map((item) => {
                        const Icon = item.icon;
                        return (
                            <BottomNavigationAction
                                key={item.id}
                                value={item.id}
                                label={shortNavLabel(item.label)}
                                icon={<Icon />}
                            />
                        );
                    })}
                    <BottomNavigationAction
                        value={MENU_ACTION_VALUE}
                        label="Menu"
                        icon={(
                            <Badge badgeContent={notificationCount > 0 ? notificationCount : null} color="error">
                                <MenuIcon />
                            </Badge>
                        )}
                    />
                </BottomNavigation>
            </Paper>
        </Box>
    );
};

export default MobileBottomNavigation;
